<script setup lang="ts">
/**
 * 字母奖池弹窗：扣 1 次机会 + 20 积分抽字母，集齐次日领现金。
 * 盘面按源站 JackpotPop 槽位铺在 bg / bg_claim 上；外壳用刮卡同款深灰底。
 */
import { computed, nextTick, onBeforeUnmount, ref, watch } from 'vue';
import { useRouter } from 'vue-router';
import {
  claimJackpot,
  drawJackpot,
  getJackpotState,
  newJackpotRequestNo,
  type JackpotDrawResult,
  type JackpotState,
  type JackpotTask,
  type JackpotWinner,
} from '@/features/jackpot/jackpot-api';
import {
  formatJackpotCountdown,
  jackpotTaskProgressPct,
  jackpotTickerPair,
  secondsUntilJackpotReset,
  sortJackpotTasks,
} from '@/features/jackpot/jackpot-view';
import { isAuthError } from '@/shared/api/auth-failure';
import { ApiBusinessError } from '@/shared/api/result';
import { t } from '@/shared/i18n/messages';
import { oroMxCurrencyAssets } from '@/shared/theme/oro-mx-currency-assets';
import {
  jackpotLetterSrc,
  jackpotPoolLetters,
  jackpotReelLetterSrc,
  jackpotShelfIndex,
  oroMxJackpotAssets,
} from '@/shared/theme/oro-mx-jackpot-assets';
import {
  destroyJackpotLottie,
  loadJackpotLottie,
  restartJackpotLottie,
  stopJackpotLottie,
} from '@/shared/theme/oro-mx-jackpot-lottie';
import type { AnimationItem } from 'lottie-web';
import { generateJackpotReelBalls } from '@/shared/theme/oro-mx-jackpot-reel';
import { formatOroMxAmount, formatOroMxGroupedAmount, formatOroMxGroupedMoney, formatOroMxMoney } from '@/shared/theme/oro-mx-money';
import { useAppToastStore } from '@/stores/app-toast-store';
import { useAuthModalStore } from '@/stores/auth-modal-store';
import { useOroMxShellStore } from '@/stores/oro-mx-shell-store';
import { useSessionStore } from '@/stores/session-store';
import { useWalletStore } from '@/stores/wallet-store';
import OroMxJackpotScroller from './OroMxJackpotScroller.vue';

type JackpotView = 'main' | 'rules';

const shell = useOroMxShellStore();
const sessionStore = useSessionStore();
const walletStore = useWalletStore();
const authModal = useAuthModalStore();
const appToast = useAppToastStore();
const router = useRouter();

const emptyState = (): JackpotState => ({
  chancesRemaining: 0,
  claimableMinor: 0,
  countdownSec: secondsUntilJackpotReset(),
  drawCostMinor: 2000,
  pendingMinor: 0,
  pointsMinor: 0,
  pools: [],
  tasks: [],
  vipLevel: 0,
  winners: [],
});

const state = ref<JackpotState>(emptyState());
const view = ref<JackpotView>('main');
const loading = ref(false);
const drawing = ref(false);
const claiming = ref(false);
const unavailable = ref(false);
const confirmOpen = ref(false);
const prizeOpen = ref(false);
const prizeMinor = ref(0);
const prizePoolKey = ref('');
const scrollerRef = ref<InstanceType<typeof OroMxJackpotScroller> | null>(null);
const reelBalls = ref(generateJackpotReelBalls([]));
const pendingDraw = ref<JackpotDrawResult | null>(null);
const anim2Show = ref(false);
const anim3Show = ref(false);
const shakeKey = ref('');
const poolShakeKey = ref('');
const bouncePoolKey = ref('');
let anim1: AnimationItem | null = null;
let anim2: AnimationItem | null = null;
let anim3: AnimationItem | null = null;
let anim5: AnimationItem | null = null;
let drawGen = 0;
const drawTimers: number[] = [];
const loadedAt = ref(Date.now());
const nowTick = ref(Date.now());
const tickerIndex = ref(0);
const tickerSliding = ref(false);
let countdownTimer = 0;
let tickerTimer = 0;
let tickerSlideTimer = 0;
const TICKER_DELAY_MS = 2000;
const TICKER_SLIDE_MS = 500;

const busy = computed(() => drawing.value || claiming.value || loading.value);
const boardReady = computed(() => state.value.pools.length > 0);
const showClaimBar = computed(() => state.value.claimableMinor > 0);
const popClass = computed(() => ({
  JackpotPop2: true,
  'has-claim-bar': showClaimBar.value,
}));
const popBg = computed(() => oroMxJackpotAssets.bg);
const canDraw = computed(
  () =>
    sessionStore.isAuthenticated
    && !busy.value
    && !unavailable.value
    && boardReady.value
    && state.value.chancesRemaining > 0
    && state.value.pointsMinor >= state.value.drawCostMinor,
);
const canClaim = computed(
  () => sessionStore.isAuthenticated && !busy.value && state.value.claimableMinor > 0,
);
const claimAmountMinor = computed(() => state.value.claimableMinor);
const winBallSrc = computed(() => {
  const draw = pendingDraw.value;
  if (!draw) return '';
  return jackpotReelLetterSrc(draw.poolKey, draw.letter);
});
const prizeLetters = computed(() =>
  prizePoolKey.value ? jackpotPoolLetters(prizePoolKey.value) : [],
);

const displayTasks = computed(() => sortJackpotTasks(state.value.tasks));
const countdownText = computed(() => {
  const elapsed = Math.floor((nowTick.value - loadedAt.value) / 1000);
  return formatJackpotCountdown(Math.max(0, state.value.countdownSec - elapsed));
});

function tickerLabel(winner: JackpotWinner): string {
  return t('oro.jackpot.congrats', {
    amount: formatOroMxGroupedMoney(winner.prizeMinor),
    name: winner.name,
  });
}

const tickerPair = computed(() => jackpotTickerPair(state.value.winners, tickerIndex.value));
const tickerCurrent = computed(() => tickerPair.value.current);
const tickerNext = computed(() => tickerPair.value.next);

const pools = computed(() =>
  [...state.value.pools]
    .sort((left, right) => jackpotShelfIndex(left.poolKey) - jackpotShelfIndex(right.poolKey))
    .map((pool) => ({
      ...pool,
      letters: jackpotPoolLetters(pool.poolKey).map((letter, index) => ({
        collected: pool.collected[index] === true,
        index,
        letter,
        src: jackpotLetterSrc(pool.poolKey, letter, pool.collected[index] === true),
      })),
    })),
);

function showToast(message: string, kind: 'ok' | 'info' | 'error' = 'error') {
  appToast.show(message, kind);
}

function applyState(next: JackpotState) {
  state.value = {
    ...next,
    countdownSec: Number.isFinite(next.countdownSec) ? Math.max(0, next.countdownSec) : 0,
    tasks: (next.tasks ?? []).map((task) => ({
      ...task,
      progressMinor: Number.isFinite(task.progressMinor) ? Math.max(0, task.progressMinor) : 0,
    })),
    winners: next.winners ?? [],
  };
  unavailable.value = false;
  loadedAt.value = Date.now();
  nowTick.value = loadedAt.value;
  resetTickerMotion();
  if (sessionStore.token) {
    walletStore.pointsMinor = next.pointsMinor;
  }
  if (!drawing.value) {
    reelBalls.value = generateJackpotReelBalls(next.pools, { includeCollected: true });
  }
}

function resetTickerMotion() {
  if (tickerSlideTimer) window.clearTimeout(tickerSlideTimer);
  tickerSlideTimer = 0;
  tickerSliding.value = false;
  tickerIndex.value = 0;
}

function startClocks() {
  stopClocks();
  countdownTimer = window.setInterval(() => {
    nowTick.value = Date.now();
  }, 1000);
  tickerTimer = window.setInterval(() => {
    if (state.value.winners.length < 2 || tickerSliding.value) return;
    tickerSliding.value = true;
    tickerSlideTimer = window.setTimeout(() => {
      const total = state.value.winners.length;
      if (total > 1) tickerIndex.value = (tickerIndex.value + 1) % total;
      tickerSliding.value = false;
      tickerSlideTimer = 0;
    }, TICKER_SLIDE_MS);
  }, TICKER_DELAY_MS);
}

function stopClocks() {
  if (countdownTimer) window.clearInterval(countdownTimer);
  if (tickerTimer) window.clearInterval(tickerTimer);
  countdownTimer = 0;
  tickerTimer = 0;
  resetTickerMotion();
}

function taskProgressLabel(task: JackpotTask): string {
  return `${formatOroMxGroupedMoney(task.progressMinor)} / ${formatOroMxGroupedMoney(task.thresholdMinor)}`;
}

function clearDrawTimers() {
  for (const timer of drawTimers) window.clearTimeout(timer);
  drawTimers.length = 0;
}

function afterDraw(ms: number, fn: () => void) {
  const timer = window.setTimeout(fn, ms);
  drawTimers.push(timer);
}

function sleep(ms: number) {
  return new Promise<void>((resolve) => {
    afterDraw(ms, resolve);
  });
}

function clearFlyBalls() {
  document.querySelectorAll('.oro-mx-fly-ball').forEach((node) => node.remove());
}

function hideBurstGlow() {
  anim2Show.value = false;
  anim3Show.value = false;
  stopJackpotLottie(anim1);
  stopJackpotLottie(anim2);
  stopJackpotLottie(anim3);
}

function resetDrawVisual() {
  drawGen += 1;
  clearDrawTimers();
  clearFlyBalls();
  hideBurstGlow();
  pendingDraw.value = null;
  shakeKey.value = '';
  poolShakeKey.value = '';
  bouncePoolKey.value = '';
  drawing.value = false;
  scrollerRef.value?.cancel();
}

function destroyCenterLottie() {
  destroyJackpotLottie(anim1);
  destroyJackpotLottie(anim2);
  destroyJackpotLottie(anim3);
  anim1 = null;
  anim2 = null;
  anim3 = null;
}

function destroyPrizeLottie() {
  destroyJackpotLottie(anim5);
  anim5 = null;
}

async function initCenterLottie() {
  destroyCenterLottie();
  await nextTick();
  anim1 = loadJackpotLottie(document.getElementById('jackpot_lottie1'), oroMxJackpotAssets.vfx1, true);
  anim2 = loadJackpotLottie(document.getElementById('jackpot_lottie2'), oroMxJackpotAssets.vfx2, false);
  anim3 = loadJackpotLottie(document.getElementById('jackpot_lottie3'), oroMxJackpotAssets.vfx3, true);
}

async function initPrizeLottie() {
  destroyPrizeLottie();
  await nextTick();
  const item = loadJackpotLottie(
    document.getElementById('jackpot_lottie5'),
    oroMxJackpotAssets.vfx5,
    true,
  );
  anim5 = item;
  item?.play();
}

async function loadState() {
  loading.value = true;
  try {
    const next = await getJackpotState(sessionStore.token || undefined);
    applyState(next);
    return next;
  } catch (error) {
    if (isAuthError(error)) {
      state.value = emptyState();
      loadedAt.value = Date.now();
      nowTick.value = loadedAt.value;
      return null;
    }
    unavailable.value = true;
    showToast(t('oro.jackpot.unavailable'));
    return null;
  } finally {
    loading.value = false;
  }
}

function playError(error: unknown): string {
  if (error instanceof ApiBusinessError) {
    if (error.code === 'WALLET_INSUFFICIENT_BALANCE') return t('oro.jackpot.insufficient');
    if (error.code === 'ACTIVITY_NOT_CLAIMABLE') return t('oro.jackpot.noChances');
    if (error.code === 'ACTIVITY_NOT_FOUND') return t('oro.jackpot.unavailable');
  }
  return t('oro.jackpot.unavailable');
}

function askDraw() {
  if (drawing.value || claiming.value || unavailable.value) return;
  if (!sessionStore.isAuthenticated || !sessionStore.token) {
    authModal.openAuth({ mode: 'login' });
    return;
  }
  if (state.value.chancesRemaining < 1) {
    showToast(t('oro.jackpot.noChances'));
    return;
  }
  if (state.value.pointsMinor < state.value.drawCostMinor) {
    showToast(t('oro.jackpot.insufficient'));
    return;
  }
  confirmOpen.value = true;
}

async function confirmDraw() {
  if (!sessionStore.token || drawing.value) return;
  confirmOpen.value = false;
  drawing.value = true;
  const gen = (drawGen += 1);
  pendingDraw.value = null;
  hideBurstGlow();
  restartJackpotLottie(anim1);
  reelBalls.value = generateJackpotReelBalls(state.value.pools, { includeCollected: true });
  await nextTick();
  scrollerRef.value?.start();
  const startedAt = Date.now();
  try {
    const result = await drawJackpot(sessionStore.token, newJackpotRequestNo());
    if (gen !== drawGen || !shell.jackpotOpen) return;
    const wait = Math.max(0, 500 - (Date.now() - startedAt));
    if (wait) await sleep(wait);
    if (gen !== drawGen || !shell.jackpotOpen) return;
    pendingDraw.value = result;
    state.value = {
      ...state.value,
      chancesRemaining: result.chancesAfter,
      claimableMinor:
        result.completed && result.prizeMinor > 0
          ? state.value.claimableMinor + result.prizeMinor
          : state.value.claimableMinor,
      pointsMinor: result.pointsAfter,
    };
    walletStore.pointsMinor = result.pointsAfter;
    await nextTick();
    if (scrollerRef.value) {
      scrollerRef.value.stop({
        index: result.letterIndex,
        letter: result.letter,
        poolKey: result.poolKey,
      });
    } else {
      onReelFinish();
    }
  } catch (error) {
    if (gen !== drawGen) return;
    hideBurstGlow();
    drawing.value = false;
    scrollerRef.value?.cancel();
    if (isAuthError(error)) return;
    showToast(playError(error));
  }
}

function flyDrawnBall(poolKey: string, letterIndex: number, gen: number) {
  afterDraw(250, () => {
    if (gen !== drawGen || !shell.jackpotOpen) return;
    const from = document.getElementById('oro-mx-jackpot-start-dom');
    const to = document.getElementById(`${poolKey}_${letterIndex}`);
    const source = document.getElementById('oro-mx-jackpot-start-img');
    if (!from || !to || !source) return;
    const startBox = from.getBoundingClientRect();
    const destBox = to.getBoundingClientRect();
    const clone = document.createElement('div');
    clone.className = 'oro-mx-fly-ball';
    clone.innerHTML = source.innerHTML;
    clone.style.position = 'fixed';
    clone.style.left = '0';
    clone.style.top = '0';
    clone.style.zIndex = '9999';
    clone.style.pointerEvents = 'none';
    document.body.appendChild(clone);
    const srcImg = source.querySelector('img');
    const cloneImg = clone.querySelector('img');
    if (srcImg && cloneImg) {
      cloneImg.style.width = `${Math.max(srcImg.getBoundingClientRect().width, 1)}px`;
    }
    const cloneBox = clone.getBoundingClientRect();
    const x0 = startBox.left + startBox.width / 2 - cloneBox.width / 2;
    const y0 = startBox.top + startBox.height / 2 - cloneBox.height / 2;
    clone.style.transition = 'none';
    clone.style.transform = `translate(${x0}px, ${y0}px) scale(0.7)`;
    clone.getBoundingClientRect();
    afterDraw(50, () => {
      if (gen !== drawGen) {
        clone.remove();
        return;
      }
      clone.style.transition = 'transform 0.3s';
      clone.style.transform = `translate(${x0}px, ${y0}px) scale(0.9)`;
      afterDraw(300, () => {
        if (gen !== drawGen) {
          clone.remove();
          return;
        }
        const live = document.getElementById(`${poolKey}_${letterIndex}`)?.getBoundingClientRect();
        const end = live ?? destBox;
        const x1 = end.left + end.width / 2 - cloneBox.width / 2;
        const y1 = end.top + end.height / 2 - cloneBox.height / 2;
        clone.style.transition = 'transform 0.2s';
        clone.style.transform = `translate(${x1}px, ${y1}px) scale(0.5)`;
        afterDraw(250, () => clone.remove());
      });
    });
  });
}

function onReelFinish() {
  const gen = drawGen;
  const result = pendingDraw.value;
  if (!result || !shell.jackpotOpen) {
    drawing.value = false;
    hideBurstGlow();
    return;
  }
  stopJackpotLottie(anim1);
  anim2Show.value = true;
  anim3Show.value = false;
  restartJackpotLottie(anim2);
  afterDraw(450, () => {
    if (gen !== drawGen || !shell.jackpotOpen) return;
    anim2Show.value = false;
    anim3Show.value = true;
    restartJackpotLottie(anim3);
  });
  flyDrawnBall(result.poolKey, result.letterIndex, gen);
  afterDraw(800, () => {
    if (gen !== drawGen || !shell.jackpotOpen) return;
    state.value = {
      ...state.value,
      pools: state.value.pools.map((pool) =>
        pool.poolKey === result.poolKey
          ? {
              ...pool,
              collected: result.completed
                ? jackpotPoolLetters(result.poolKey).map(() => true)
                : result.collected,
            }
          : pool,
      ),
    };
    const completed = result.completed && result.prizeMinor > 0;
    if (completed) {
      poolShakeKey.value = result.poolKey;
      afterDraw(200, () => {
        if (gen === drawGen) bouncePoolKey.value = result.poolKey;
      });
      afterDraw(1000, () => {
        if (gen !== drawGen) return;
        poolShakeKey.value = '';
        bouncePoolKey.value = '';
      });
      prizeMinor.value = result.prizeMinor;
      prizePoolKey.value = result.poolKey;
      afterDraw(1200, () => {
        if (gen === drawGen && shell.jackpotOpen) prizeOpen.value = true;
      });
    } else {
      shakeKey.value = `${result.poolKey}_${result.letterIndex}`;
      afterDraw(400, () => {
        if (gen === drawGen) shakeKey.value = '';
      });
    }
    const settle = completed ? 1200 : 450;
    afterDraw(settle, () => {
      if (gen !== drawGen || !shell.jackpotOpen) return;
      pendingDraw.value = null;
      drawing.value = false;
      reelBalls.value = generateJackpotReelBalls(state.value.pools, { includeCollected: true });
      void loadState();
    });
  });
}

async function onPrizeClaim() {
  prizeOpen.value = false;
  await onClaim();
}

async function onClaim() {
  if (!canClaim.value) {
    return;
  }
  if (!sessionStore.token) return;
  claiming.value = true;
  try {
    const result = await claimJackpot(sessionStore.token);
    showToast(t('oro.jackpot.claimOk', { amount: formatOroMxMoney(result.rewardMinor) }), 'ok');
    await Promise.all([loadState(), walletStore.loadBalance()]);
  } catch (error) {
    if (isAuthError(error)) return;
    showToast(playError(error));
  } finally {
    claiming.value = false;
  }
}

function taskIcon(task: JackpotTask): string {
  if (task.condition === 'BET') return oroMxJackpotAssets.taskBet;
  if (task.condition === 'DEPOSIT') return oroMxJackpotAssets.taskDeposit;
  return oroMxJackpotAssets.taskLogin;
}

function taskFlLabel(task: JackpotTask): string {
  if (task.condition === 'BET') return t('oro.jackpot.taskBetBtn');
  if (task.condition === 'DEPOSIT') return t('oro.jackpot.taskDepositBtn');
  return '';
}

function taskActionLabel(task: JackpotTask): string {
  if (task.granted) return t('oro.jackpot.taskDone');
  if (task.condition === 'BET') return t('oro.jackpot.taskBetBtn');
  if (task.condition === 'DEPOSIT') return t('oro.jackpot.taskDepositBtn');
  return t('oro.jackpot.taskGo');
}

function onTaskGo(task: JackpotTask) {
  if (task.granted || task.condition === 'VIP_LOGIN') return;
  shell.closeJackpot();
  if (task.condition === 'DEPOSIT') {
    void router.push('/deposit');
    return;
  }
  void router.push('/');
}

function toggleRules() {
  view.value = view.value === 'rules' ? 'main' : 'rules';
}

function onClose() {
  confirmOpen.value = false;
  prizeOpen.value = false;
  resetDrawVisual();
  shell.closeJackpot();
}

watch(countdownText, (text, previous) => {
  if (text !== '00:00:00' || previous === '00:00:00' || !shell.jackpotOpen) return;
  void loadState();
});

watch(
  () => shell.jackpotOpen,
  async (open) => {
    if (!open) {
      view.value = 'main';
      confirmOpen.value = false;
      prizeOpen.value = false;
      resetDrawVisual();
      destroyCenterLottie();
      destroyPrizeLottie();
      stopClocks();
      return;
    }
    view.value = 'main';
    startClocks();
    await loadState();
    await initCenterLottie();
  },
  { immediate: true },
);

watch(prizeOpen, async (open) => {
  if (!open) {
    destroyPrizeLottie();
    return;
  }
  await initPrizeLottie();
});

onBeforeUnmount(() => {
  resetDrawVisual();
  destroyCenterLottie();
  destroyPrizeLottie();
  stopClocks();
});
</script>

<template>
  <div
    v-if="shell.jackpotOpen"
    class="jackpot-overlay"
    role="dialog"
    aria-modal="true"
    :aria-label="t('oro.jackpot.title')"
  >
    <button class="jackpot-overlay__hit" type="button" :aria-label="t('oro.common.close')" @click="onClose" />
    <div class="jackpot-shell" @click.stop>
      <header class="jackpot-header">
        <h2>{{ t('oro.jackpot.title') }}</h2>
        <div class="jackpot-header__tools">
          <button type="button" :aria-label="t('oro.common.rules')" @click="toggleRules">
            <img :src="oroMxJackpotAssets.details" alt="" />
          </button>
          <button type="button" :aria-label="t('oro.common.close')" @click="onClose">
            <img :src="oroMxJackpotAssets.close" alt="" />
          </button>
        </div>
      </header>

      <div
        class="JackpotPop"
        :class="popClass"
        :style="{ backgroundImage: `url('${popBg}')` }"
      >
        <div v-if="showClaimBar" class="div1">
          <div class="fl">
            <p class="p1">{{ t('oro.jackpot.todayReward') }}</p>
            <p class="p2">
              <span class="ic"><img :src="oroMxCurrencyAssets.cash" alt="" /></span>
              {{ formatOroMxAmount(claimAmountMinor) }}
            </p>
          </div>
          <div class="fr">
            <button
              class="claim_btn"
              :class="{ claim_btn_hui: !canClaim }"
              type="button"
              @click="onClaim"
            >
              <span class="sp1">{{ claiming ? '…' : t('oro.jackpot.claim') }}</span>
              <span class="btn_img">
                <img
                  :src="canClaim ? oroMxJackpotAssets.claim : oroMxJackpotAssets.claimDisabled"
                  alt=""
                />
              </span>
              <span v-if="canClaim" class="btn_wheel_shou">
                <img class="pointer" :src="oroMxJackpotAssets.finger" alt="" />
              </span>
            </button>
          </div>
        </div>

        <div class="div2">
          <div class="ti_tixian">
            <div class="withdraw_swiper_tag">
              <div class="w_ic">
                <img :src="oroMxJackpotAssets.trumpet" alt="" />
              </div>
              <div
                class="wd_swiper"
                :style="{ backgroundImage: `url('${oroMxJackpotAssets.withdrawBg}')` }"
              >
                <div v-if="tickerCurrent" class="ticker-viewport">
                  <div class="ticker-track" :class="{ 'is-sliding': tickerSliding }">
                    <p class="ticker">{{ tickerLabel(tickerCurrent) }}</p>
                    <p v-if="tickerNext" class="ticker" aria-hidden="true">{{ tickerLabel(tickerNext) }}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="div3">
          <p v-if="loading && !boardReady" class="jackpot-empty">{{ t('oro.promo.loading') }}</p>
          <p v-else-if="unavailable && !pools.length" class="jackpot-empty">
            {{ t('oro.jackpot.unavailable') }}
          </p>
          <div v-else class="letters_box">
            <div
              v-for="pool in pools"
              :key="pool.poolKey"
              class="item"
              :class="{ 'win-shake': poolShakeKey === pool.poolKey }"
            >
              <div class="text">
                <div class="p2">
                  <span class="sp1">
                    <span class="ic"><img :src="oroMxCurrencyAssets.cash" alt="" /></span>
                    <span class="sp11">{{ formatOroMxGroupedAmount(pool.minMinor) }}</span>
                  </span>
                  <span class="sp2">-</span>
                  <span class="sp1">
                    <span class="ic"><img :src="oroMxCurrencyAssets.cash" alt="" /></span>
                    <span class="sp11">{{ formatOroMxGroupedAmount(pool.maxMinor) }}</span>
                  </span>
                </div>
                <div class="p1">
                  <div
                    v-for="slot in pool.letters"
                    :id="`${pool.poolKey}_${slot.index}`"
                    :key="`${pool.poolKey}-${slot.index}`"
                    class="sp1"
                    :class="{
                      'win-shake2': shakeKey === `${pool.poolKey}_${slot.index}`,
                    }"
                  >
                    <span
                      class="ball"
                      :class="{
                        'ball-bounce':
                          bouncePoolKey === pool.poolKey,
                      }"
                      :style="
                        bouncePoolKey === pool.poolKey
                          ? { animationDelay: `${slot.index * 90}ms` }
                          : undefined
                      "
                    >
                      <img :src="slot.src" :alt="slot.letter" />
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="box" aria-hidden="true">
          <div class="box_con">
            <div class="scroll_mask">
              <div />
              <div />
              <div />
            </div>
            <OroMxJackpotScroller
              ref="scrollerRef"
              :balls="reelBalls"
              @finish="onReelFinish"
            />
            <div class="jackpot_amin">
              <div id="oro-mx-jackpot-start-img" class="Animation_img">
                <img v-if="winBallSrc" :src="winBallSrc" alt="" />
              </div>
              <div id="oro-mx-jackpot-start-dom" class="amin_box">
                <div id="jackpot_lottie1" class="lottie_img lottie_img1" />
                <div
                  v-show="anim2Show"
                  id="jackpot_lottie2"
                  class="lottie_img lottie_img2"
                />
                <div
                  v-show="anim3Show"
                  id="jackpot_lottie3"
                  class="lottie_img lottie_img3"
                />
              </div>
            </div>
          </div>
        </div>

        <div class="chances_num2">
          <p class="p1">
            <span class="ic"><img :src="oroMxCurrencyAssets.bonus" alt="" /></span>
            <span class="sp1">{{ t('oro.jackpot.bonus') }}</span>
          </p>
          <p class="p2">{{ formatOroMxAmount(state.pointsMinor) }}</p>
        </div>
        <div class="chances_num">
          <p class="p1">{{ t('oro.jackpot.spinsLeft') }}</p>
          <p class="p2">{{ state.chancesRemaining }}</p>
        </div>

        <div class="div4">
          <button
            class="draw_btn"
            type="button"
            :disabled="busy || unavailable"
            @click="askDraw"
          >
            <span class="img">
              <img
                :src="canDraw ? oroMxJackpotAssets.draw : oroMxJackpotAssets.drawDisabled"
                alt=""
              />
            </span>
            <span class="sp1">
              <span class="ic"><img :src="oroMxJackpotAssets.bonusChip" alt="" /></span>
              <span class="sp11">{{ formatOroMxAmount(state.drawCostMinor) }}</span>
            </span>
            <span v-if="canDraw && !drawing" class="btn_wheel_shou">
              <img class="pointer" :src="oroMxJackpotAssets.finger" alt="" />
            </span>
          </button>
        </div>

        <section v-if="state.tasks.length" class="task_div">
          <div class="ti">
            <span class="ic"><img :src="oroMxJackpotAssets.starLeft" alt="" /></span>
            <span class="sp1">{{ t('oro.jackpot.tasks') }}</span>
            <span class="ic"><img :src="oroMxJackpotAssets.starRight" alt="" /></span>
          </div>
          <div class="list">
            <div v-for="task in displayTasks" :key="task.taskKey" class="item">
              <div v-if="task.condition === 'VIP_LOGIN'" class="item_con">
                <div class="fl">
                  <span class="ic"><img :src="oroMxJackpotAssets.taskLogin" alt="" /></span>
                  <span class="sp1">{{ t('oro.jackpot.taskLoginLabel') }}</span>
                </div>
                <div class="n_text">
                  <div class="vip_info">
                    <span class="vip_info_img">
                      <img :src="oroMxJackpotAssets.vipBg" alt="" />
                    </span>
                    <span class="vip_info_text">
                      <span class="vip">VIP{{ state.vipLevel }}</span>
                    </span>
                  </div>
                  <span class="times times2">+{{ task.chances }}</span>
                </div>
                <div class="fr">
                  <span class="task_btn task_btn_ghost" aria-hidden="true" />
                </div>
              </div>
              <div v-else class="item_con">
                <div class="fl">
                  <span class="ic"><img :src="taskIcon(task)" alt="" /></span>
                  <span class="sp1">{{ taskFlLabel(task) }}</span>
                </div>
                <div class="n_text">
                  <div class="vip-experience_newv2">
                    <div class="vip-experience_new_conv2">
                      <div class="vip-experience_new_con_textv2">
                        <span class="sp1">{{ taskProgressLabel(task) }}</span>
                      </div>
                      <div
                        class="vip-experience_new_con_progressv2"
                        :style="{ width: `${jackpotTaskProgressPct(task)}%` }"
                      />
                    </div>
                  </div>
                  <span class="times">+{{ task.chances }}</span>
                </div>
                <div class="fr">
                  <button
                    class="task_btn"
                    type="button"
                    :disabled="task.granted"
                    @click="onTaskGo(task)"
                  >
                    <span class="sp1">{{ taskActionLabel(task) }}</span>
                    <span class="btn_img">
                      <img
                        :src="
                          task.granted
                            ? oroMxJackpotAssets.taskBtnDisabled
                            : oroMxJackpotAssets.taskBtn
                        "
                        alt=""
                      />
                    </span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>
        <div class="time_con">
          <span class="sp1">{{ t('oro.jackpot.reset') }}</span>
          <span class="number">{{ countdownText }}</span>
        </div>
      </div>

      <div v-if="view === 'rules'" class="jackpot-rules" role="document">
        <h3>{{ t('oro.jackpot.rulesTitle') }}</h3>
        <p>{{ t('oro.jackpot.rulesCost') }}</p>
        <p>{{ t('oro.jackpot.rulesCollect') }}</p>
        <p>{{ t('oro.jackpot.rulesReset') }}</p>
        <p>{{ t('oro.jackpot.rulesClaim') }}</p>
        <p>{{ t('oro.jackpot.rulesPoints') }}</p>
      </div>

      <div v-if="confirmOpen" class="jackpot-dialog" role="dialog">
        <div class="jackpot-dialog__panel">
          <p>{{ t('oro.jackpot.confirmDraw', { amount: formatOroMxAmount(state.drawCostMinor) }) }}</p>
          <div class="jackpot-dialog__actions">
            <button type="button" @click="confirmOpen = false">{{ t('oro.common.close') }}</button>
            <button class="jackpot-dialog__ok" type="button" @click="confirmDraw">
              {{ t('oro.common.confirm') }}
            </button>
          </div>
        </div>
      </div>

      <div v-if="prizeOpen" class="jackpot-dialog" role="dialog">
        <div
          class="drawPop"
          :style="{ backgroundImage: `url('${oroMxJackpotAssets.popBg}')` }"
        >
          <p class="drawPop__title">{{ t('oro.jackpot.complete') }}</p>
          <div class="drawPop_top">
            <div class="n_text">
              <div
                v-for="(letter, index) in prizeLetters"
                :key="`${prizePoolKey}-${index}-${letter}`"
                class="item"
                :style="{ zIndex: prizeLetters.length - index }"
              >
                <img :src="jackpotLetterSrc(prizePoolKey, letter, true)" alt="" />
              </div>
              <div id="jackpot_lottie5" class="lottie_img5" />
            </div>
          </div>
          <div class="drawPop_b">
            <div
              class="drawPop_b_num"
              :style="{ backgroundImage: `url('${oroMxJackpotAssets.popAmount}')` }"
            >
              <div class="ic">
                <img class="currency_img" :src="oroMxCurrencyAssets.cash" alt="" />
              </div>
              <div class="num">{{ formatOroMxMoney(prizeMinor) }}</div>
            </div>
            <button class="lingqi_btn" type="button" @click="onPrizeClaim">
              <span class="image">
                <img :src="oroMxJackpotAssets.popButton" alt="" />
                <span class="sp1">{{ t('oro.jackpot.claim') }}</span>
              </span>
            </button>
          </div>
        </div>
      </div>

    </div>
  </div>
</template>

<style scoped>
.jackpot-overlay {
  --jp: calc(1rem * 8 / 3);
  position: absolute;
  inset: 0;
  z-index: 3600;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  overflow: auto;
  padding: calc(0.16 * var(--jp)) calc(0.1 * var(--jp));
  background: rgb(0 0 0 / 55%);
}

.jackpot-overlay__hit {
  position: absolute;
  inset: 0;
  padding: 0;
  background: transparent;
  border: 0;
}

.jackpot-shell {
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  box-sizing: border-box;
  width: calc(7.3 * var(--jp) + 0.64rem);
  margin: auto 0;
  padding: 0.53333rem 0.32rem;
  background: #232325;
  border-radius: 0.53333rem;
}

.jackpot-header {
  display: flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: space-between;
  box-sizing: border-box;
  width: calc(7.3 * var(--jp));
  margin-bottom: 0.53333rem;
  padding: 0;
  background: transparent;
}

.jackpot-header h2 {
  margin: 0;
  color: #fff;
  font-size: calc(0.36 * var(--jp));
  font-weight: 700;
  line-height: 1;
}

.jackpot-header__tools {
  display: flex;
  gap: calc(0.2 * var(--jp));
  align-items: center;
}

.jackpot-header__tools button {
  display: flex;
  width: calc(0.4 * var(--jp));
  height: calc(0.4 * var(--jp));
  padding: 0;
  background: transparent;
  border: 0;
}

.jackpot-header__tools img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.JackpotPop {
  --jp: calc(1rem * 8 / 3);
  position: relative;
  box-sizing: border-box;
  width: calc(7.3 * var(--jp));
  height: calc(7.3 * 1060 / 730 * var(--jp));
  overflow: hidden;
  color: #fff;
  background-color: #2a0a12;
  background-position: center;
  background-size: 100% 100%;
  background-repeat: no-repeat;
  border-radius: 0.21333rem;
}

.JackpotPop .div1,
.JackpotPop .div2,
.JackpotPop .div3,
.JackpotPop .div4,
.JackpotPop .box,
.JackpotPop .task_div,
.JackpotPop .time_con,
.JackpotPop .chances_num,
.JackpotPop .chances_num2 {
  position: absolute;
  box-sizing: border-box;
}

.JackpotPop .div1 {
  top: calc(0.62 * var(--jp));
  left: calc(0.2 * var(--jp));
  z-index: 3;
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: calc(6.9 * var(--jp));
}

.JackpotPop .div1 .fl {
  display: flex;
  flex-wrap: wrap;
  gap: calc(0.08 * var(--jp)) calc(0.16 * var(--jp));
  align-items: center;
  margin-right: calc(0.2 * var(--jp));
}

.JackpotPop .div1 .fl .p1,
.JackpotPop .div1 .fl .p2,
.JackpotPop .chances_num .p1,
.JackpotPop .chances_num .p2,
.JackpotPop .chances_num2 .p1,
.JackpotPop .chances_num2 .p2 {
  margin: 0;
  line-height: 1.15;
}

.JackpotPop .div1 .fl .p1 {
  margin-bottom: 0;
  color: #fff;
  font-size: calc(0.24 * var(--jp));
}

.JackpotPop .div1 .fl .p2 {
  display: flex;
  align-items: center;
  color: #ffe45a;
  font-size: calc(0.42 * var(--jp));
  font-weight: 700;
}

.JackpotPop .div1 .fl .p2 .ic {
  display: flex;
  margin-right: calc(0.08 * var(--jp));
}

.JackpotPop .div1 .fl .p2 .ic img {
  height: calc(0.42 * var(--jp));
  width: auto;
}

.JackpotPop .div1 .fr {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.JackpotPop .div1 .fr .claim_btn_time {
  position: absolute;
  top: calc(0.74 * var(--jp));
  left: 50%;
  margin: 0;
  color: #fff;
  font-size: calc(0.22 * var(--jp));
  font-weight: 400;
  line-height: 1;
  letter-spacing: 0.02em;
  transform: translateX(-50%);
  white-space: nowrap;
}

.JackpotPop .div1 .fr .claim_btn {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: calc(1.65 * var(--jp));
  height: calc(0.48 * var(--jp));
  padding: 0;
  color: #733006;
  font-size: calc(0.32 * var(--jp));
  font-weight: 700;
  background: transparent;
  border: 0;
}

.JackpotPop .div1 .fr .claim_btn.claim_btn_hui {
  width: calc(1.65 * var(--jp));
  color: #d9d9d9;
  font-size: calc(0.28 * var(--jp));
}

.JackpotPop .claim_btn .btn_img,
.JackpotPop .draw_btn .img,
.JackpotPop .task_btn .btn_img {
  position: absolute;
  inset: 0;
}

.JackpotPop .claim_btn .btn_img img,
.JackpotPop .draw_btn .img img,
.JackpotPop .task_btn .btn_img img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: fill;
}

.JackpotPop .claim_btn .sp1,
.JackpotPop .draw_btn .sp1,
.JackpotPop .task_btn .sp1 {
  position: relative;
  z-index: 1;
}

.JackpotPop .div2 {
  top: calc(0.14 * var(--jp));
  right: 0;
  left: 0;
  z-index: 2;
  padding: 0 calc(0.2 * var(--jp));
}

.JackpotPop .div2 .ti_tixian,
.JackpotPop .div2 .withdraw_swiper_tag {
  display: flex;
  align-items: center;
  width: 100%;
}

.JackpotPop .div2 .w_ic {
  position: relative;
  left: calc(0.1 * var(--jp));
  z-index: 1;
  flex-shrink: 0;
  width: calc(0.3 * var(--jp));
}

.JackpotPop .div2 .w_ic img {
  display: block;
  width: 100%;
  height: auto;
}

.JackpotPop .div2 .wd_swiper {
  overflow: hidden;
  flex: 1;
  height: calc(0.44 * var(--jp));
  margin-left: calc(0.08 * var(--jp));
  background-position: center;
  background-size: 100% 100%;
  background-repeat: no-repeat;
}

.JackpotPop .div2 .ticker-viewport {
  overflow: hidden;
  width: 100%;
  height: 100%;
}

.JackpotPop .div2 .ticker-track {
  display: flex;
  flex-direction: column;
  width: 100%;
  transform: translate3d(0, 0, 0);
  will-change: transform;
}

.JackpotPop .div2 .ticker-track.is-sliding {
  transform: translate3d(0, calc(-0.44 * var(--jp)), 0);
  transition: transform 500ms ease;
}

.JackpotPop .div2 .ticker {
  box-sizing: border-box;
  flex: 0 0 calc(0.44 * var(--jp));
  width: 100%;
  height: calc(0.44 * var(--jp));
  margin: 0;
  padding: 0 calc(0.2 * var(--jp));
  overflow: hidden;
  color: #fff;
  font-size: calc(0.24 * var(--jp));
  line-height: calc(0.44 * var(--jp));
  text-align: center;
  white-space: nowrap;
  text-overflow: ellipsis;
}

.JackpotPop .div3 {
  top: calc(3.32 * var(--jp));
  right: 0;
  left: 0;
  padding: 0 calc(0.15 * var(--jp));
}

.JackpotPop2 .div3 {
  top: calc(0.86 * var(--jp));
}

.JackpotPop2.has-claim-bar .div3 {
  top: calc(1.28 * var(--jp));
}

.JackpotPopOld .div3 {
  top: calc(2.95 * var(--jp));
}

.JackpotPop .letters_box {
  display: flex;
  flex-wrap: wrap;
}

.JackpotPop .letters_box .item {
  display: flex;
  width: 50%;
  justify-content: center;
}

.JackpotPop .div3 .letters_box .item:first-child,
.JackpotPop .div3 .letters_box .item:nth-child(2) {
  margin-bottom: calc(0.42 * var(--jp));
}

.JackpotPopOld .div3 .letters_box .item:first-child,
.JackpotPopOld .div3 .letters_box .item:nth-child(2) {
  margin-bottom: calc(0.28 * var(--jp));
}

.JackpotPop .div3 .letters_box .item .text {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.JackpotPop .div3 .letters_box .item .text .p2 {
  display: flex;
  align-items: center;
  margin-bottom: calc(0.14 * var(--jp));
  color: #ffe45a;
  font-weight: 700;
}

.JackpotPop .div3 .letters_box .item .text .p2 .sp1 {
  display: flex;
  align-items: center;
}

.JackpotPop .div3 .letters_box .item .text .p2 .sp1 .ic {
  display: flex;
  margin-right: calc(0.05 * var(--jp));
}

.JackpotPop .div3 .letters_box .item .text .p2 .sp1 .ic img {
  height: calc(0.32 * var(--jp));
  width: auto;
}

.JackpotPop .div3 .letters_box .item .text .p2 .sp1 .sp11,
.JackpotPop .div3 .letters_box .item .text .p2 .sp2 {
  font-size: calc(0.32 * var(--jp));
}

.JackpotPop .div3 .letters_box .item .text .p2 .sp2 {
  margin: 0 calc(0.05 * var(--jp));
}

.JackpotPop .div3 .letters_box .item .text .p1 {
  position: relative;
  display: flex;
  justify-content: center;
  font-size: calc(0.32 * var(--jp));
}

.JackpotPop .div3 .letters_box .item:nth-child(3) .text .p1 {
  left: calc(-0.02 * var(--jp));
}

.JackpotPop .div3 .letters_box .item:nth-child(4) .text .p1 {
  left: calc(-0.1 * var(--jp));
}

.JackpotPop .div3 .letters_box .item .text .p1 .sp1 {
  position: relative;
}

.JackpotPop .div3 .letters_box .item .text .p1 .sp1:first-child {
  left: calc(0.3 * var(--jp));
}

.JackpotPop .div3 .letters_box .item .text .p1 .sp1:nth-child(2) {
  left: calc(0.15 * var(--jp));
}

.JackpotPop .div3 .letters_box .item .text .p1 .sp1:nth-child(4) {
  left: calc(-0.15 * var(--jp));
}

.JackpotPop .div3 .letters_box .item .text .p1 .sp1:nth-child(5) {
  left: calc(-0.3 * var(--jp));
}

.JackpotPop .letters_box .ball {
  display: flex;
  width: calc(0.76 * var(--jp));
  height: calc(0.76 * var(--jp));
}

.JackpotPop .letters_box .ball img {
  display: block;
  width: calc(0.76 * var(--jp));
  height: auto;
}

.JackpotPop .letters_box .sp1.win-shake2 {
  z-index: 10;
  animation: winShake2 0.4s ease;
}

.JackpotPop .letters_box .item.win-shake {
  animation: winShake 0.35s ease;
}

.JackpotPop .letters_box .ball.ball-bounce {
  animation: ballBounce 0.5s ease;
}

@keyframes winShake2 {
  0%,
  100% { transform: scale(1); }
  50% { transform: scale(1.2); }
}

@keyframes winShake {
  0% { transform: scale(1); }
  20% { transform: scale(1.1); }
  40% { transform: scale(0.95); }
  60% { transform: scale(1.05); }
  100% { transform: scale(1); }
}

@keyframes ballBounce {
  0%,
  100% { transform: translateY(0); }
  40% { transform: translateY(calc(-0.14 * var(--jp))); }
  70% { transform: translateY(calc(0.04 * var(--jp))); }
}

.jackpot-empty {
  margin: calc(0.8 * var(--jp)) 0;
  color: #d9d9d9;
  font-size: calc(0.28 * var(--jp));
  text-align: center;
}

.JackpotPop .box {
  right: 0;
  bottom: calc(4.92 * var(--jp));
  left: 0;
  height: calc(1.36 * var(--jp));
  pointer-events: none;
}

.JackpotPop .box_con {
  position: relative;
  height: 100%;
}

.JackpotPop .box_con .scroll_mask {
  position: absolute;
  top: calc(0.1 * var(--jp));
  left: calc(0.14 * var(--jp));
  z-index: 3;
  display: flex;
  justify-content: space-between;
  width: 95.8%;
  height: calc(1.16 * var(--jp));
  pointer-events: none;
}

.JackpotPop .box_con .scroll_mask div:first-child {
  width: calc(3 * var(--jp));
  background: linear-gradient(to right, rgb(42 8 12 / 88%), rgb(42 8 12 / 0%));
}

.JackpotPop .box_con .scroll_mask div:nth-child(2) {
  width: calc(1.6 * var(--jp));
}

.JackpotPop .box_con .scroll_mask div:nth-child(3) {
  width: calc(3 * var(--jp));
  background: linear-gradient(to left, rgb(42 8 12 / 88%), rgb(42 8 12 / 0%));
}

.JackpotPop .box .box_con .jackpot_amin {
  position: absolute;
  top: calc(-0.43 * var(--jp));
  left: calc(2.51 * var(--jp));
  z-index: 4;
  width: calc(2.29 * var(--jp));
  height: calc(2.21 * var(--jp));
  pointer-events: none;
}

.JackpotPop .box .box_con .jackpot_amin .amin_box {
  position: relative;
  width: calc(2.29 * var(--jp));
  height: calc(2.21 * var(--jp));
}

.JackpotPop .box .box_con .jackpot_amin .Animation_img {
  position: absolute;
  top: 50%;
  left: 50%;
  z-index: 0;
  width: calc(0.76 * var(--jp));
  opacity: 0;
  transform: translate(-50%, -50%);
}

.JackpotPop .box .box_con .jackpot_amin .Animation_img img {
  display: block;
  width: 100%;
  height: auto;
}

.JackpotPop .box .box_con .jackpot_amin .lottie_img {
  position: absolute;
  top: 0;
  left: 0;
  z-index: 2;
  width: 100%;
  height: 100%;
  pointer-events: none;
}

.JackpotPop .box .box_con .jackpot_amin .lottie_img svg {
  display: block;
  width: 100%;
  height: 100%;
}

.JackpotPop .box .box_con .jackpot_amin .lottie_img2 {
  top: calc(-0.08 * var(--jp));
}

.JackpotPop .box .box_con .jackpot_amin .lottie_img3 {
  left: calc(-0.04 * var(--jp));
}

.JackpotPop .chances_num2 {
  bottom: calc(4.1 * var(--jp));
  left: calc(0.155 * var(--jp));
  width: calc(2 * var(--jp));
  color: #fff;
  text-align: center;
}

.JackpotPop .chances_num {
  right: calc(0.29 * var(--jp));
  bottom: calc(4.08 * var(--jp));
  width: calc(2 * var(--jp));
  color: #fff;
  text-align: center;
}

.JackpotPop .chances_num2 .p1,
.JackpotPop .chances_num2 .p2,
.JackpotPop .chances_num .p1 {
  font-size: calc(0.24 * var(--jp));
}

.JackpotPop .chances_num .p2 {
  font-size: calc(0.28 * var(--jp));
  font-weight: 700;
}

.JackpotPop .chances_num2 .p1 {
  display: flex;
  align-items: center;
  justify-content: center;
}

.JackpotPop .chances_num2 .p1 .ic {
  display: flex;
  margin-right: calc(0.04 * var(--jp));
}

.JackpotPop .chances_num2 .p1 .ic img {
  height: calc(0.24 * var(--jp));
  width: auto;
}

.JackpotPop .div4 {
  bottom: calc(3.5 * var(--jp));
  left: 50%;
  transform: translateX(-50%);
}

.JackpotPop .div4 .draw_btn {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: calc(2.61 * var(--jp));
  height: calc(1.1 * var(--jp));
  padding: 0;
  color: #fff;
  font-size: calc(0.36 * var(--jp));
  font-weight: 700;
  background: transparent;
  border: 0;
}

.JackpotPop .div4 .draw_btn .sp1,
.JackpotPop .div4 .draw_btn .sp1 .ic img {
  height: calc(0.5 * var(--jp));
}

.JackpotPop .div4 .draw_btn .sp1 {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: calc(0.06 * var(--jp));
  transform: translateY(calc(-0.2 * var(--jp)));
}

.JackpotPop .div4 .draw_btn .sp1 .ic {
  display: flex;
}

.JackpotPop .div4 .draw_btn .sp1 .ic img {
  width: auto;
}

.JackpotPop .btn_wheel_shou {
  position: absolute;
  top: calc(0.39 * var(--jp));
  left: calc(1.7 * var(--jp));
  z-index: 2;
  width: calc(1 * var(--jp));
  pointer-events: none;
  animation: jp-finger 1s ease-in-out infinite;
}

.JackpotPop .div1 .btn_wheel_shou {
  top: calc(0.18 * var(--jp));
  left: calc(1.35 * var(--jp));
}

.JackpotPop .btn_wheel_shou img {
  display: block;
  width: 100%;
  height: auto;
}

@keyframes jp-finger {
  0%,
  100% { transform: translate(0, 0); }
  50% { transform: translate(calc(-0.12 * var(--jp)), calc(-0.12 * var(--jp))); }
}

.JackpotPop .task_div {
  right: 0;
  bottom: calc(0.65 * var(--jp));
  left: 0;
}

.JackpotPop .time_con {
  right: 0;
  bottom: calc(-0.04 * var(--jp));
  left: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: calc(0.14 * var(--jp)) 0;
  color: #ababab;
  font-size: calc(0.26 * var(--jp));
  line-height: 1;
}

.JackpotPop .time_con .sp1 {
  margin-right: calc(0.1 * var(--jp));
}

.JackpotPop .time_con .number {
  position: relative;
  top: 0;
  color: #ababab;
  font-size: calc(0.26 * var(--jp));
  font-weight: 400;
}

.JackpotPopOld .task_div {
  bottom: calc(0.5 * var(--jp));
}

.JackpotPop .task_div .ti {
  position: relative;
  top: calc(0.02 * var(--jp));
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: calc(0.09 * var(--jp));
  color: #ffe7a0;
  font-size: calc(0.32 * var(--jp));
  font-weight: 700;
}

.JackpotPop .task_div .ti .sp1 {
  margin: 0 calc(0.1 * var(--jp));
}

.JackpotPop .task_div .ti .ic img {
  display: block;
  height: calc(0.2 * var(--jp));
  width: auto;
}

.JackpotPop .task_div .list {
  padding: 0 calc(0.65 * var(--jp));
}

.JackpotPop .task_div .list .item {
  box-sizing: border-box;
  height: calc(0.718 * var(--jp));
  padding: calc(0.1 * var(--jp)) 0;
}

.JackpotPop .task_div .list .item_con {
  display: flex;
  align-items: center;
  height: 100%;
}

.JackpotPop .task_div .list .item_con .fl {
  display: flex;
  flex-shrink: 0;
  align-items: center;
  width: calc(1.7 * var(--jp));
  margin-right: calc(0.05 * var(--jp));
  font-size: calc(0.26 * var(--jp));
}

.JackpotPop .task_div .list .item_con .fl .ic {
  display: flex;
  margin-right: calc(0.06 * var(--jp));
}

.JackpotPop .task_div .list .item_con .fl .ic img {
  height: calc(0.32 * var(--jp));
  width: auto;
}

.JackpotPop .task_div .list .item_con .n_text {
  display: flex;
  flex: 1;
  min-width: 0;
  align-items: center;
}

.JackpotPop .task_div .list .item_con .n_text .vip-experience_newv2 {
  flex: 1;
  min-width: 0;
  margin-right: calc(0.15 * var(--jp));
}

.JackpotPop .task_div .list .item_con .n_text .vip-experience_new_conv2 {
  position: relative;
  overflow: hidden;
  height: calc(0.32 * var(--jp));
  background: rgb(0 0 0 / 35%);
  border-radius: calc(0.16 * var(--jp));
}

.JackpotPop .task_div .list .item_con .n_text .vip-experience_new_con_progressv2 {
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  background: linear-gradient(90deg, #c48a12, #ffe45a);
}

.JackpotPop .task_div .list .item_con .n_text .vip-experience_newv2 .vip-experience_new_con_textv2 {
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: #fff;
  font-size: calc(0.22 * var(--jp));
  font-weight: 700;
}

.JackpotPop .task_div .list .item_con .n_text .vip_info {
  display: flex;
  flex: 1;
  min-width: 0;
  justify-content: center;
}

.JackpotPop .task_div .list .item_con .n_text .times {
  flex-shrink: 0;
  color: #ffe45a;
  font-size: calc(0.26 * var(--jp));
  font-weight: 700;
}

.JackpotPop .task_div .list .item_con .fr {
  margin-left: calc(0.15 * var(--jp));
}

.JackpotPop .task_div .list .item_con .fr .task_btn {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: calc(1.26 * var(--jp));
  height: calc(0.51 * var(--jp));
  padding: 0;
  color: #733006;
  font-size: calc(0.24 * var(--jp));
  font-weight: 700;
  background: transparent;
  border: 0;
}

.JackpotPop .task_div .list .item_con .fr .task_btn:disabled {
  color: #d9d9d9;
}

.JackpotPop .task_div .list .item_con .fr .task_btn.task_btn_ghost {
  visibility: hidden;
  pointer-events: none;
}

.JackpotPop .vip_info {
  position: relative;
  top: calc(0.04 * var(--jp));
  width: calc(1.45 * var(--jp));
  height: calc(0.384 * var(--jp));
  font-size: calc(0.3 * var(--jp));
}

.JackpotPop .vip_info .vip_info_img img {
  display: block;
  height: calc(0.34 * var(--jp));
  width: auto;
}

.JackpotPop .vip_info .vip_info_text {
  position: absolute;
  top: 0;
  left: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  width: calc(1.2 * var(--jp));
  height: calc(0.32 * var(--jp));
  transform: translateX(-50%);
}

.JackpotPop .vip_info .vip_info_text .vip {
  color: #6a3e01;
  font-size: calc(0.22 * var(--jp));
  font-weight: 700;
}

.JackpotPop .times.times2 {
  margin-left: calc(0.12 * var(--jp));
  color: #ffe45a;
  font-size: calc(0.26 * var(--jp));
  font-weight: 700;
}

.jackpot-rules {
  position: absolute;
  inset: calc(0.7 * var(--jp)) calc(0.2 * var(--jp)) calc(0.2 * var(--jp));
  z-index: 6;
  overflow: auto;
  padding: calc(0.35 * var(--jp));
  color: #ececec;
  font-size: calc(0.26 * var(--jp));
  line-height: 1.45;
  background: rgb(20 8 10 / 94%);
  border-radius: calc(0.16 * var(--jp));
}

.jackpot-rules h3 {
  margin: 0 0 calc(0.2 * var(--jp));
  color: #ffe7a0;
  font-size: calc(0.32 * var(--jp));
}

.jackpot-rules p {
  margin: 0 0 calc(0.16 * var(--jp));
}

.jackpot-dialog {
  position: absolute;
  inset: 0;
  z-index: 8;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: calc(0.32 * var(--jp));
  background: rgb(0 0 0 / 55%);
}

.jackpot-dialog__panel {
  width: calc(5.6 * var(--jp));
  padding: calc(0.36 * var(--jp)) calc(0.28 * var(--jp));
  color: #fff;
  text-align: center;
  background: #2e2e36;
  border-radius: calc(0.16 * var(--jp));
}

.jackpot-dialog__panel p {
  margin: 0 0 calc(0.24 * var(--jp));
  font-size: calc(0.28 * var(--jp));
  line-height: 1.4;
}

.jackpot-dialog__actions {
  display: flex;
  gap: calc(0.16 * var(--jp));
  justify-content: center;
}

.jackpot-dialog__actions button,
.jackpot-dialog__ok {
  min-width: calc(1.8 * var(--jp));
  height: calc(0.72 * var(--jp));
  color: #fff;
  font-size: calc(0.26 * var(--jp));
  background: #4a4a52;
  border: 0;
  border-radius: calc(0.12 * var(--jp));
}

.jackpot-dialog__ok {
  color: #1a1a1e;
  font-weight: 700;
  background: linear-gradient(180deg, #ffe45a, #f0b21a);
}

.drawPop {
  box-sizing: border-box;
  width: calc(5.6 * var(--jp));
  padding: calc(0.28 * var(--jp)) calc(0.2 * var(--jp)) calc(0.36 * var(--jp));
  background-position: center;
  background-size: 100% 100%;
  background-repeat: no-repeat;
  text-align: center;
}

.drawPop__title {
  margin: 0 0 calc(0.08 * var(--jp));
  color: #fff;
  font-size: calc(0.32 * var(--jp));
  font-weight: 700;
}

.drawPop_top {
  position: relative;
  left: calc(-0.06 * var(--jp));
  padding: calc(0.2 * var(--jp)) 0 calc(0.12 * var(--jp));
}

.drawPop_top .n_text {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
}

.drawPop_top .n_text .lottie_img5 {
  position: absolute;
  top: calc(-0.5 * var(--jp));
  left: -25%;
  width: 150%;
  pointer-events: none;
}

.drawPop_top .item + .item {
  margin-left: calc(-0.14 * var(--jp));
}

.drawPop_top .item img {
  display: block;
  width: calc(0.76 * var(--jp));
  height: auto;
}

.drawPop_b {
  position: relative;
  left: calc(-0.06 * var(--jp));
}

.drawPop_b_num {
  display: flex;
  flex-direction: row;
  align-items: center;
  width: calc(3.76 * var(--jp));
  height: calc(0.8 * var(--jp));
  margin: 0 auto calc(0.12 * var(--jp));
  overflow: hidden;
  background-position: center;
  background-size: 100% 100%;
  background-repeat: no-repeat;
}

.drawPop_b_num .ic {
  display: flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  width: calc(1.12 * var(--jp));
  height: calc(0.8 * var(--jp));
}

.drawPop_b_num .ic .currency_img {
  width: calc(0.6 * var(--jp));
  height: calc(0.6 * var(--jp));
  object-fit: contain;
}

.drawPop_b_num .num {
  display: flex;
  align-items: center;
  justify-content: center;
  width: calc(2.54 * var(--jp));
  height: calc(0.8 * var(--jp));
  color: #733006;
  font-size: calc(0.4 * var(--jp));
  font-weight: 700;
  line-height: 1;
}

.lingqi_btn {
  display: flex;
  justify-content: center;
  width: 100%;
  padding: calc(0.4 * var(--jp)) 0 0;
  background: transparent;
  border: 0;
}

.lingqi_btn .image {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: calc(2.3 * var(--jp));
  height: calc(0.8 * var(--jp));
}

.lingqi_btn .image img {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
}

.lingqi_btn .image .sp1 {
  position: relative;
  z-index: 1;
  color: #733006;
  font-size: calc(0.36 * var(--jp));
  font-weight: 700;
}

@media (prefers-reduced-motion: reduce) {
  .JackpotPop .btn_wheel_shou,
  .JackpotPop .letters_box .sp1.win-shake2,
  .JackpotPop .box .box_con .jackpot_amin .lottie_img1 svg,
  .JackpotPop .box .box_con .jackpot_amin .lottie_img2 svg,
  .JackpotPop .box .box_con .jackpot_amin .lottie_img3 svg {
    animation: none;
  }
}
</style>

<style>
.oro-mx-fly-ball img {
  display: block;
  width: 46px;
  height: auto;
}
</style>
